import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { startCheckout, track } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";

const CALENDLY_URL =
  "https://calendly.com/luke-bromantics/first-walk?hide_gdpr_banner=1&background_color=f5f3ef&text_color=161816&primary_color=4a5d4e";

export default function Book() {
  const [scheduled, setScheduled] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showPaymentPanel, setShowPaymentPanel] = useState(false);
  const checkoutStarted = useRef(false);
  const paymentPending = useRef(false);
  const calendlyContainer = useRef(null);

  // Calendly official inline embed: widget.js is loaded once globally in
  // public/index.html; initialize the scheduler into the container below.
  useEffect(() => {
    const container = calendlyContainer.current;
    if (!container || !window.Calendly?.initInlineWidget) return undefined;
    window.Calendly.initInlineWidget({
      url: CALENDLY_URL,
      parentElement: container,
    });
    return () => {
      container.replaceChildren();
    };
  }, []);

  const goToPayment = async () => {
    setFailed(false);
    paymentPending.current = true;
    try {
      await startCheckout();
    } catch (e) {
      paymentPending.current = false;
      setFailed(true);
      setShowPaymentPanel(true);
      toast.error("Couldn't start the secure payment. Please try again.");
    }
  };

  useEffect(() => {
    const onMessage = (e) => {
      let data = e.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (err) {
          return;
        }
      }
      if (!data || typeof data.event !== "string") return;
      if (!data.event.startsWith("calendly.")) return;
      // diagnostic beacon: records genuine Calendly events + origins live
      track("calendly_message", { event: data.event, origin: e.origin || "none" });
      if (data.event !== "calendly.event_scheduled") return;
      setScheduled(true);
      track("time_chosen");
      if (!checkoutStarted.current) {
        checkoutStarted.current = true;
        goToPayment();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the redirect to Stripe has not happened within a few seconds of the
  // booking being detected, reveal the prominent payment panel.
  useEffect(() => {
    if (!scheduled) return undefined;
    const timer = setTimeout(() => {
      if (!paymentPending.current) setShowPaymentPanel(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, [scheduled]);

  return (
    <main className="min-h-screen bg-paper" data-testid="book-page">
      <Nav />
      <div className="mx-auto max-w-3xl px-5 md:px-8 pt-32 md:pt-40 pb-24 md:pb-32">
        <h1
          className="font-serif text-4xl md:text-5xl tracking-tight text-ink"
          data-testid="book-heading"
        >
          The First Walk
        </h1>
        <p
          className="mt-4 text-sm tracking-[0.2em] uppercase font-semibold text-pine"
          data-testid="book-microcopy"
        >
          30 minutes · £60
        </p>
        <p className="mt-8 text-base md:text-lg leading-relaxed text-ink/70">
          A calm, focused conversation about what’s happening and what feels
          most important right now.
        </p>
        <p
          className="mt-5 text-base md:text-lg leading-relaxed text-ink/70"
          data-testid="book-how-it-works"
        >
          Choose a time below, then confirm your booking with payment. You
          don’t need to prepare anything — I’ll call you on WhatsApp at the
          number you provide.
        </p>
        <div className="mt-12" data-testid="calendly-step">
          <div
            ref={calendlyContainer}
            className="w-full"
            style={{ minWidth: "320px", height: "720px" }}
            data-testid="calendly-embed"
          />
          {!scheduled && (
            <p className="mt-5 text-sm text-ink/50">
              Chosen your time but the payment page didn’t open?{" "}
              <button
                type="button"
                onClick={goToPayment}
                data-testid="manual-payment-link"
                className="underline underline-offset-4 hover:text-ink transition-colors duration-300"
              >
                Continue to secure payment — £60
              </button>
            </p>
          )}
        </div>
        {scheduled && (
          <div className="mt-12 text-center" data-testid="time-chosen-status">
            <p className="font-serif text-2xl md:text-3xl tracking-tight text-ink">
              Time chosen.
            </p>
            {!showPaymentPanel && (
              <p
                className="mt-4 text-base text-ink/60"
                data-testid="payment-redirect-status"
              >
                Taking you to secure payment — £60…
              </p>
            )}
          </div>
        )}
        {scheduled && showPaymentPanel && (
          <div
            className="mt-8 bg-ink px-8 py-12 md:py-14 text-center"
            data-testid="one-last-step-panel"
          >
            <p className="font-serif text-2xl md:text-3xl tracking-tight text-paper">
              One last step
            </p>
            <p
              className="mt-4 mx-auto max-w-md text-base leading-relaxed text-paper/70"
              data-testid="one-last-step-copy"
            >
              Your time has been selected. Complete your £60 payment to confirm
              your First Walk.
            </p>
            {failed && (
              <p
                className="mt-4 text-sm text-paper/60"
                data-testid="payment-error-note"
              >
                Something went wrong starting the secure payment. Please try
                again.
              </p>
            )}
            <button
              type="button"
              onClick={goToPayment}
              data-testid="confirm-first-walk-button"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-paper text-ink px-8 py-4 text-base font-semibold tracking-wide transition-colors duration-300 hover:bg-pine hover:text-paper"
            >
              Confirm my First Walk — £60
            </button>
          </div>
        )}
        <p
          className="mt-6 max-w-xl text-xs md:text-sm leading-relaxed text-ink/45"
          data-testid="book-disclaimer"
        >
          By booking, you confirm that you understand Walk It Through does not
          provide legal, financial, medical, therapeutic or emergency advice or
          services.
        </p>
      </div>
      <Footer />
    </main>
  );
}
